import React from 'react';
import { Conversation } from '../fasades/conversation';
import { DialogManager } from '../fasades/dialogmanager';
import { SpinnerManager } from '../fasades/spinnermanager';
import { ToastManager, TOAST_TYPES } from '../fasades/toastmanager';
import { ApiError, IModel, ITraining, ITrainingChunk, TrainingMethod } from '../chatbot/api/llmapi';
import { LogSliderComp } from '../chatbot/components/logslidercomp';
import { SliderComp } from '../chatbot/components/slidercomp';
import { Message } from '../chatbot/chat/message';
import { TrainingGraph } from '../chatbot/components/graph/traininggraph';
import { AxisType } from '../chatbot/components/graph/axis';
import { Series } from '../chatbot/components/graph/series';
import { ToggleSwitch } from '../chatbot/components/toggleswitch';


type TrainingState = {
    method           : TrainingMethod,
    learningRate     : number,
    learningSteps    : number,
    epsilon          : number,
    beta             : number,
    showAccuracy     : boolean,
    showAdvanced     : boolean,
    showTrainingLoss : boolean,
    series           : Series[],
    targetAccuracy   : number,
    width            : number,
    training         : boolean
};

type TrainProps = {
    model: IModel;
    message: Message;

    onCancel?: () => void;
    onApply?: (data: IModel) => void;
};

export class TrainDialog extends React.Component<TrainProps, TrainingState> {
    dialogRef = React.createRef<HTMLDivElement>();
    accuracy  : Series[];
    loss      : Series[];

    constructor(props: TrainProps) {
        super(props);

        if (props.model.method === undefined) {
            props.model.method = TrainingMethod.GCE;
        }

        this.accuracy = [];
        this.loss     = [];

        this.state = {
            beta             : props.model.beta,
            epsilon          : props.model.epsilon,
            method           : props.model.method!,
            learningRate     : props.model.learning_rate,
            learningSteps    : props.model.learning_steps,
            targetAccuracy   : 80,
            showAccuracy     : true,
            showAdvanced     : false,
            showTrainingLoss : false,
            series           : this.accuracy.concat(this.loss),
            width            : 600,
            training         : false
        };
    }

    addSeries() {
        if (this.state.showAccuracy) {
            if (this.state.showTrainingLoss) {
                this.setState({ series : this.accuracy.concat(this.loss) });
            }
            else {
                this.setState({ series : this.accuracy });
            }
        }
        else if (this.state.showTrainingLoss) {
            this.setState({ series : this.loss });
        }
    }

    componentDidMount()
    {
        this.createSeries();
        window.addEventListener('resize', this.handleResize.bind(this));
        this.handleResize();

    }

    componentWillUnmount()
    {
        window.removeEventListener('resize', this.handleResize.bind(this));
    }

    createSeries()
    {
        // Get Message
        let messages : Message[] = [];
        if (this.state.method === TrainingMethod.SFT) {
            messages.push(this.props.message);
        }
        else {
            messages = this.props.message.parent!.replies;
        }

        const sortedMessages = [...messages].sort((a, b) => b.rating - a.rating);
        for (let i = 0; i < sortedMessages.length; i++) {
            sortedMessages[i].place = i;
        }

        if (messages.length !== this.accuracy.length) {
            const colors = ["#008000", "#004000", "#80f000", "#ffff00", "#ff8000", "#ff0000"];
            this.accuracy = [];
            this.loss     = [];

            for (let i = 0; i < messages.length; i++) {
                const label      = this.getLabelName(messages[i].content);
                const colorIndex = Math.round(messages[i].place! * (colors.length - 1) / (messages.length - 1));
                const color      = colors[colorIndex];

                this.accuracy.push(new Series(label, messages[i].rating, 'Accuracy',       color, [], AxisType.Left));
                this.loss.push(new Series(label,     messages[i].rating, 'Traingloss PPL', color, [5,5], AxisType.Right));
            }
        }
        this.addSeries();
    }

    getLabelName(content: string): string {
        let result: string;

        const startIndex = content.indexOf('<think>');
        if (startIndex === -1) {
            // No <think> tag found
            result = content;
        } else {
            const endIndex = content.indexOf('</think>', startIndex);
            if (endIndex === -1) {
                // No matching closing tag
                result = content;
            } else {
                result = content.slice(0, startIndex) + content.slice(endIndex + 8);
            }
        }

        result = result.trim();

        //Truncate to avoid heavy rendering cost
        const MAX_LENGTH = 100;
        if (result.length > MAX_LENGTH) {
            result = result.slice(0, MAX_LENGTH) + "...";
        }

        return result;
    }

    handleCancel() {
        if (this.props.onCancel) {
            this.props.onCancel();
        }

        this.handleClose();
    }

    handleClose() {
        DialogManager.close();
    }

    handleResize() 
    {
        let width = window.innerWidth - 150;
        if (width > 600) {
            width = 600;
        }
        else if (width < 286) {
            width = 286;
        }

        this.setState({ width: width});
    }

    handleChangeLearingRate(rate: number) {
        this.setState({ learningRate: rate });
    }

    handleLearningSteps(e: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ learningSteps: parseInt(e.target.value) });
    }

    async handleTraining() {
        const parameters: ITraining = {
            method: this.state.method,
            epsilon: this.state.epsilon,
            beta: this.state.beta,
            learning_rate: this.state.learningRate,
            learning_steps: this.state.learningSteps
        }

        if (this.state.targetAccuracy !== 80) {
            parameters.target_accuracy = this.state.targetAccuracy / 100;
        }

        this.props.model.epsilon = this.state.epsilon;
        this.props.model.beta = this.state.beta;
        this.props.model.learning_rate = this.state.learningRate;
        this.props.model.learning_steps = this.state.learningSteps;
        this.props.model.method = this.state.method;

        let query : Message[] = [];
        let replies : Message[] = [];

        if (this.state.method === TrainingMethod.SFT) {
            query = this.props.message.getPriorMessages();
            query.push(this.props.message);
        }
        else {
            query   = this.props.message.getPriorMessages();
            replies = this.props.message.parent!.replies;
        }


        this.createSeries();
        this.setState({training : true});
        SpinnerManager.setState(true);
        try {
            await Conversation.api.train!(this.props.model, query, replies, parameters, this.updateGraph.bind(this));
            Conversation.fetchUserCredits(true);
        }
        catch(error) {
            if (error instanceof ApiError) {
                ToastManager.addToast(error.message, TOAST_TYPES.ERROR, -1);
            }
            else {
                console.log(error);
            }
        }
        SpinnerManager.setState(false);
        this.setState({training : false});

        if (this.props.onApply) {
            this.props.onApply(this.props.model);
        }
    };

    handleSelectMethod(e: React.ChangeEvent<HTMLSelectElement>) {
        this.accuracy = [];
        this.loss     = [];

        if (e.target.value === "sft") {
            this.setState({ method: TrainingMethod.SFT });
        }
        else if (e.target.value === "grpo") {
            this.setState({ method: TrainingMethod.GRPO });
        }
        else if (e.target.value === "dpo") {
            this.setState({ method: TrainingMethod.DPO });
        }
        else if (e.target.value === "gce") {
            this.setState({ method: TrainingMethod.GCE });
        }

        this.createSeries();
    };

    handleShowAccuracy(value : boolean) 
    {
        let update : any;
        if (!value && !this.state.showTrainingLoss) {
            update = {
                showTrainingLoss : true,
                showAccuracy     : value
            };
        }
        else {
            update = {
                showAccuracy : value 
            };
        }

        this.setState(update, this.addSeries.bind(this));
    }

    handleShowTrainingloss(value : boolean) 
    {
        let update : any;
        if (!value && !this.state.showAccuracy) {
            update = {
                showTrainingLoss : value,
                showAccuracy     : true
            };
        }
        else {
            update = {
                showTrainingLoss : value
            };
        }

        this.addSeries();
        this.setState(update, this.addSeries.bind(this));
    }

    renderParameters() : React.JSX.Element {
        return <>
            { this.state.showAdvanced && (
                <>
                <label className="labelStyle" htmlFor="model">Training method</label>
                <select
                    className="inputStyle"
                    id="model"
                    name="selectedModel"
                    value={this.state.method}
                    onChange={(e) => this.handleSelectMethod(e)}
                    required>
                    <option value="" disabled>Select a model</option>
                    <option key={"dpo"} value={"dpo"}>{"DPO"}</option>
                    <option key={"grpo"} value={"grpo"}>{"GRPO"}</option>
                    <option key={"sft"} value={"sft"}>{"SFT"}</option>
                    <option key={"gce"} value={"gce"}>{"Cross Entropy"}</option>
                </select>
                </>
            )}

            <label className="labelStyle" htmlFor="steps">Learning Steps</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <input
                    style={{ flex: 1 }}
                    id="steps"
                    name="steps"
                    type="range"
                    min={1}
                    max={100}
                    step={1}
                    value={this.state.learningSteps}
                    onChange={(e) => this.handleLearningSteps(e)}
                />
                <span style={{ minWidth: '2.5rem', textAlign: 'center', fontWeight: 'bold' }}>
                    {this.state.learningSteps}
                </span>
            </div>

            { this.state.method === TrainingMethod.GCE && 
            <SliderComp
                name="Target Accuracy"
                input={this.state.targetAccuracy}
                steps={300}
                stepSize={1}
                offWhen={80}
                minValue={80}
                maxValue={100}
                onChange={(value: number) => { this.setState({ targetAccuracy: value }); }}
            />
            }

            { this.state.showAdvanced && (
                <>
                <LogSliderComp
                    name="Learning Rate"
                    input={this.state.learningRate}
                    steps={300}
                    minValue={1E-6}
                    maxValue={1E-3}
                    onChange={(value: number) => { this.handleChangeLearingRate(value); }}
                />

                { this.state.method === TrainingMethod.GRPO && 
                <SliderComp
                    name="Epsilon"
                    input={this.state.epsilon}
                    steps={300}
                    minValue={0}
                    maxValue={1}
                    onChange={(value: number) => { this.setState({ epsilon: value }); }}
                />
                }

                { (this.state.method === TrainingMethod.GRPO || this.state.method === TrainingMethod.DPO) && 
                <SliderComp
                    name="Beta"
                    input={this.state.beta}
                    steps={300}
                    minValue={0}
                    maxValue={1}
                    onChange={(value: number) => { this.setState({ beta: value }); }}
                />
                }
            </>
            )}
        </>
    }

    render() {
        return (
            <div ref={this.dialogRef} className="dialogStyle">
                <h2 style={{ marginTop: 0 }}>Training Parameters</h2>
                <form method="dialog" onSubmit={e => e.preventDefault()}>
                    <div>
                        <label className="labelStyle" htmlFor="name">Name</label>
                        <input
                            className="inputStyle"
                            id="name"
                            name="name"
                            type="text"
                            value={this.props.model.name}
                            required
                            readOnly
                        />
                        <ToggleSwitch name="Advanced Options" isToggled={this.state.showAdvanced} onChange={(value) => this.setState({showAdvanced : !this.state.showAdvanced }) } />

                        { this.renderParameters() }

                        { this.state.showAdvanced && (
                            <>
                            <ToggleSwitch name="Show Accuracy" isToggled={this.state.showAccuracy} onChange={(value) => this.handleShowAccuracy(value)} />
                            <ToggleSwitch name="Show Training Loss(PPL)" isToggled={this.state.showTrainingLoss} onChange={(value) => this.handleShowTrainingloss(value)} />
                            </>
                        )}
                    </div>
                    <TrainingGraph series={ this.state.series } width={ this.state.width} minWidth={340}/>

                    <div className="buttonRowStyle">
                        <button type="button" className="buttonStyle cancelStyle" onClick={() => this.handleCancel()}>
                            Close
                        </button>
                        <button disabled={this.state.training } type="button" className={ this.state.training ? "buttonStyle" : "buttonStyle saveStyle" } onClick={() => this.handleTraining()}>
                            Train
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    static async showDialog(message: Message, model: IModel): Promise<void> {
        return new Promise((resolve) => {
            DialogManager.open(
                <TrainDialog
                    model={model}
                    message={message}
                    onApply={(data: IModel) => { Object.assign(model, data); resolve() }}
                    onCancel={() => { resolve() }} />
            );
        });
    }

    updateGraph(chunk : ITrainingChunk)
    {
        SpinnerManager.setState(false);

        if (chunk.type === "performance") {
            const data = chunk.performance;
            const index = data.id - 1;

            if (index >= 0 && index < this.accuracy.length) {
                let accuracy = this.accuracy[index];
                accuracy.append(100 * data.accuracy);
                accuracy.accuracy = data.accuracy;
                accuracy.unit     = 'Accuracy : ' + (100*accuracy.accuracy).toFixed(1) + '%';
                let loss     = this.loss[index];
                loss.append(data.loss);
            }

            if (this.state.showAccuracy) {
                if (this.state.showTrainingLoss) {
                    this.setState({ series : this.accuracy.concat(this.loss) });
                }
                else {
                    this.setState({ series : this.accuracy });
                }
            }
            else if (this.state.showTrainingLoss) {
                this.setState({ series : this.loss });
            }
        }
    }
}

